import React, { useRef, useEffect, useState, useCallback } from "react";
import "./VideoPlayer.css";
import { YOUTUBE_VIDEO_BUFFER } from "../../constants/general";

interface YouTubePlayerProps {
  videoId: string;
  title?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  duration?: number;
  onEnded: () => void;
  onError?: (error: string) => void;
  onTimeUpdate?: (current: number, duration: number) => void;
  className?: string;
  fillMode?: "contain" | "cover";
}

interface YouTubePlayerAPI {
  loadVideoById: (videoId: string) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  mute: () => void;
  unMute: () => void;
  setVolume: (volume: number) => void;
  getPlayerState: () => number;
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
}

// Define YouTube event types
interface YouTubePlayerEvent {
  target: YouTubePlayerAPI;
  data?: number;
}

// Track global YouTube API loading state
let youtubeAPILoaded = false;
let youtubeAPILoading = false;
let instanceCounter = 0;
let apiLoadAttempts = 0;
const MAX_API_LOAD_ATTEMPTS = 3;

declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => YouTubePlayerAPI;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
        UNSTARTED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

// Development-only fallback for when YouTube API fails to load
const DEV_FALLBACK_VIDEOS: Record<string, string> = {
  // Map YouTube IDs to local videos for testing
  "qxDGqON-Uzk": "/videos/placeholder1.mp4",
  tQHAwV9B8hQ: "/videos/placeholder2.mp4",
  // Add default fallback
  default: "/videos/default-placeholder.mp4",
};

function getDevFallbackVideo(videoId: string): string {
  return DEV_FALLBACK_VIDEOS[videoId] ?? DEV_FALLBACK_VIDEOS.default;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  title = "YouTube Video",
  autoplay = true,
  muted = true,
  loop = false,
  duration = 30,
  onEnded,
  onError,
  onTimeUpdate,
  className = "youtube-player",
  fillMode = "cover",
}) => {
  const instanceId = useRef(`yt-${++instanceCounter}`);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayerAPI | null>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const completedRef = useRef(false);
  const mountedRef = useRef(true);
  const [playerReady, setPlayerReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isYouTubeAPIReady, setIsYouTubeAPIReady] = useState(
    Boolean(window.YT && window.YT.Player)
  );

  console.log(
    `🎬 YouTubePlayer[${instanceId.current}] Initializing for "${title}"`
  );

  // Load YouTube API with retry mechanism
  const loadYouTubeAPI = useCallback(() => {
    if (isYouTubeAPIReady || youtubeAPILoaded) {
      setIsYouTubeAPIReady(true);
      return Promise.resolve();
    }

    if (youtubeAPILoading) {
      return new Promise<void>((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (youtubeAPILoaded) {
            clearInterval(checkInterval);
            setIsYouTubeAPIReady(true);
            resolve();
          } else if (apiLoadAttempts > MAX_API_LOAD_ATTEMPTS) {
            clearInterval(checkInterval);
            reject(new Error("YouTube API load timeout"));
          }
        }, 200);
      });
    }

    apiLoadAttempts++;
    youtubeAPILoading = true;

    return new Promise<void>((resolve, reject) => {
      // Try two different methods to load the API
      // Method 1: Direct script insertion (standard)
      const loadViaScript = () => {
        const isLocalDev = window.location.hostname === "localhost";
        try {
          const script = document.createElement("script");
          // For development environments, try using a CORS proxy
          script.src = isLocalDev
            ? "https://cors-anywhere.herokuapp.com/https://www.youtube.com/iframe_api"
            : "https://www.youtube.com/iframe_api";
          script.async = true;

          const originalCallback = window.onYouTubeIframeAPIReady;

          window.onYouTubeIframeAPIReady = () => {
            youtubeAPILoaded = true;
            youtubeAPILoading = false;
            setIsYouTubeAPIReady(true);

            // Call any previously set callback
            if (typeof originalCallback === "function") {
              originalCallback();
            }

            resolve();
          };

          script.onerror = () => {
            console.warn("YouTube API loading failed via script tag");
            if (isLocalDev) {
              // For local dev, try alternative method
              loadViaFetch();
            } else {
              reject(new Error("YouTube API loading failed"));
            }
          };

          document.head.appendChild(script);
        } catch (e) {
          if (isLocalDev) {
            loadViaFetch();
          } else {
            reject(e);
          }
        }
      };

      // Method 2: Dynamic eval (fallback for CORS issues)
      const loadViaFetch = () => {
        try {
          // Create a script element dynamically
          const script = document.createElement("script");

          // Set its text content directly to avoid CORS
          script.textContent = `
            if (!window.YT) {
              window.YT = {loading: 0,loaded: 0};
            }
            if (!window.YTConfig) {
              window.YTConfig = {'host': 'https://www.youtube.com'};
            }
            if (!YT.loading) {
              YT.loading = 1;
              (function(){
                var l = [];
                YT.ready = function(f) {
                  if (YT.loaded) {
                    f();
                  } else {
                    l.push(f);
                  }
                };
                window.onYTReady = function() {
                  YT.loaded = 1;
                  for (var i = 0; i < l.length; i++) {
                    try {
                      l[i]();
                    } catch (e) {}
                  }
                };
                YT.setConfig = function(c) {
                  for (var k in c) {
                    if (c.hasOwnProperty(k)) {
                      YTConfig[k] = c[k];
                    }
                  }
                };
                var a = document.createElement('script');
                a.type = 'text/javascript';
                a.id = 'www-widgetapi-script';
                a.src = 'https://s.ytimg.com/yts/jsbin/www-widgetapi-vflS50iB-/www-widgetapi.js';
                a.async = true;
                var b = document.getElementsByTagName('script')[0];
                b.parentNode.insertBefore(a, b);
              })();
            }
          `;

          // Append script to document
          document.head.appendChild(script);

          // Poll for YT being available
          const checkYTInterval = setInterval(() => {
            if (window.YT && window.YT.Player) {
              clearInterval(checkYTInterval);
              youtubeAPILoaded = true;
              youtubeAPILoading = false;
              setIsYouTubeAPIReady(true);
              resolve();
            }
          }, 100);

          // Set timeout for this approach
          setTimeout(() => {
            clearInterval(checkYTInterval);
            reject(new Error("YouTube API load timeout via fetch approach"));
          }, 5000);
        } catch (e) {
          youtubeAPILoading = false;
          let errorMessage = "Unknown error";
          if (e && typeof e === "object" && "message" in e) {
            errorMessage = (e as { message: string }).message;
          }
          reject(new Error(`Failed to load YouTube API: ${errorMessage}`));
        }
      };

      // Start with script tag approach
      loadViaScript();

      // Set a global timeout
      setTimeout(() => {
        if (!youtubeAPILoaded) {
          youtubeAPILoading = false;
          reject(new Error("YouTube API load timeout"));
        }
      }, 10000);
    });
  }, [isYouTubeAPIReady]);

  // Initialize YouTube player with enhanced error handling
  const initializePlayer = useCallback(async () => {
    // Check if component is still mounted
    if (!mountedRef.current) {
      console.log(
        `⚠️ YouTubePlayer[${instanceId.current}] Component unmounted, skipping initialization`
      );
      return;
    }

    // Clear any load errors
    setLoadError(null);

    // Check if YouTube API is ready
    if (!isYouTubeAPIReady) {
      try {
        await loadYouTubeAPI();
        if (!mountedRef.current) return;
      } catch (error) {
        console.error(
          `❌ YouTubePlayer[${instanceId.current}] Failed to load YouTube API:`,
          error
        );
        if (mountedRef.current) {
          setLoadError("Failed to load YouTube API");
          onError?.("Failed to load YouTube API");
        }
        return;
      }
    }

    // Create container ID for this specific instance
    const containerId = `youtube-player-${instanceId.current}`;

    // Check if container exists
    if (!containerRef.current) {
      console.error(
        `❌ YouTubePlayer[${instanceId.current}] Container ref not available`
      );
      if (mountedRef.current) {
        setLoadError("Player container not available");
        onError?.("Player container not available");
      }
      return;
    }

    // Clear container safely
    try {
      containerRef.current.innerHTML = "";
    } catch (e) {
      console.warn(
        `⚠️ YouTubePlayer[${instanceId.current}] Error clearing container:`,
        e
      );
    }

    // Create player container div
    const playerDiv = document.createElement("div");
    playerDiv.id = containerId;
    containerRef.current.appendChild(playerDiv);

    // Clean up existing player
    try {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    } catch (e) {
      console.warn(
        `⚠️ YouTubePlayer[${instanceId.current}] Error destroying previous player:`,
        e
      );
    }

    console.log(
      `📺 YouTubePlayer[${instanceId.current}] Creating YouTube player for videoId: ${videoId}`
    );

    try {
      // Create new YouTube player
      const player = new window.YT.Player(containerId, {
        height: "100%",
        width: "100%",
        videoId: videoId,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          // Always start muted to ensure autoplay works
          mute: 1,
          controls: 0,
          showinfo: 0,
          modestbranding: 1,
          loop: loop ? 1 : 0,
          playlist: loop ? videoId : undefined,
          origin: window.location.origin,
          enablejsapi: 1,
          rel: 0,
          fs: 0,
          playsinline: 1,
        },
        events: {
          onReady: (event: YouTubePlayerEvent) => {
            if (!mountedRef.current) return;

            console.log(
              `✅ YouTubePlayer[${instanceId.current}] YouTube player ready`
            );

            setPlayerReady(true);

            // Ensure video playback starts
            try {
              event.target.playVideo();

              // If we want unmuted, try to unmute after playback starts
              if (!muted) {
                // Unmute attempts counter
                let unmuteAttempts = 0;

                const attemptUnmute = () => {
                  if (!mountedRef.current || unmuteAttempts >= 3) return;

                  try {
                    console.log(
                      `🔊 YouTubePlayer[${
                        instanceId.current
                      }] Attempting to unmute (try ${unmuteAttempts + 1})`
                    );
                    event.target.unMute();
                    event.target.setVolume(100);
                    unmuteAttempts++;

                    // Schedule another attempt
                    if (unmuteAttempts < 3) {
                      setTimeout(attemptUnmute, 1000);
                    }
                  } catch (e) {
                    console.warn(
                      `⚠️ YouTubePlayer[${instanceId.current}] Error unmuting:`,
                      e
                    );
                  }
                };

                // First unmute attempt after a short delay
                setTimeout(attemptUnmute, 500);
              }
            } catch (e) {
              console.warn(
                `⚠️ YouTubePlayer[${instanceId.current}] Error playing video:`,
                e
              );
            }

            // Set fallback timer
            if (durationTimerRef.current) {
              clearTimeout(durationTimerRef.current);
            }

            const safetyDuration = duration + YOUTUBE_VIDEO_BUFFER; // Add buffer
            console.log(
              `⏱️ YouTubePlayer[${instanceId.current}] Setting YouTube fallback timer: ${safetyDuration}s`
            );

            durationTimerRef.current = setTimeout(() => {
              if (!mountedRef.current) return;
              console.log(
                `⏱️ YouTubePlayer[${instanceId.current}] YouTube fallback timer expired`
              );
              if (!completedRef.current) {
                completedRef.current = true;
                handleVideoEnd();
              }
            }, safetyDuration * 1000);
          },
          onStateChange: (event: YouTubePlayerEvent) => {
            if (!mountedRef.current) return;

            console.log(
              `🔄 YouTubePlayer[${instanceId.current}] State change: ${event.data}`
            );

            if (event.data === window.YT.PlayerState.ENDED) {
              console.log(
                `✅ YouTubePlayer[${instanceId.current}] YouTube video ended naturally`
              );
              if (!completedRef.current) {
                completedRef.current = true;
                handleVideoEnd();
              }
            } else if (event.data === window.YT.PlayerState.PLAYING) {
              console.log(
                `▶️ YouTubePlayer[${instanceId.current}] Playback started`
              );
            }
          },
          onError: (event: YouTubePlayerEvent) => {
            if (!mountedRef.current) return;

            const errorMsg = `YouTube error code: ${event.data}`;
            console.error(
              `❌ YouTubePlayer[${instanceId.current}] ${errorMsg}`
            );
            setLoadError(errorMsg);
            onError?.(errorMsg);

            // Proceed to next item
            if (!completedRef.current) {
              completedRef.current = true;
              handleVideoEnd();
            }
          },
        },
      });

      // Store player reference
      playerRef.current = player as unknown as YouTubePlayerAPI;
    } catch (error) {
      console.error(
        `❌ YouTubePlayer[${instanceId.current}] Error creating YouTube player:`,
        error
      );

      if (mountedRef.current) {
        setLoadError(`Failed to create YouTube player: ${error}`);
        onError?.(`Failed to create YouTube player: ${error}`);
      }

      // Proceed to next item on error
      if (!completedRef.current && mountedRef.current) {
        completedRef.current = true;
        handleVideoEnd();
      }
    }
  }, [
    videoId,
    autoplay,
    muted,
    loop,
    duration,
    isYouTubeAPIReady,
    loadYouTubeAPI,
    onError,
  ]);

  // Safely handle video end
  const handleVideoEnd = useCallback(() => {
    console.log(`🏁 YouTubePlayer[${instanceId.current}] Handling video end`);

    // Clear timers
    if (durationTimerRef.current) {
      clearTimeout(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    // Clean up player
    if (playerRef.current) {
      try {
        const player = playerRef.current;
        playerRef.current = null;
        player.destroy();
      } catch (e) {
        console.warn(
          `⚠️ YouTubePlayer[${instanceId.current}] Error destroying YouTube player:`,
          e
        );
      }
    }

    // Clean up container
    if (containerRef.current) {
      try {
        containerRef.current.innerHTML = "";
      } catch (e) {
        console.warn(
          `⚠️ YouTubePlayer[${instanceId.current}] Error clearing container:`,
          e
        );
      }
    }

    // Call onEnded with small delay
    setTimeout(() => {
      if (mountedRef.current) {
        onEnded();
      }
    }, 50);
  }, [onEnded]);

  // Time update interval for progress bar
  useEffect(() => {
    if (!playerReady || !playerRef.current || !onTimeUpdate) {
      return;
    }

    const timeUpdateInterval = setInterval(() => {
      if (playerRef.current && mountedRef.current) {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          const videoDuration = playerRef.current.getDuration();

          if (videoDuration > 0) {
            onTimeUpdate(currentTime, videoDuration);
          }
        } catch (error) {
          console.warn(
            `⚠️ YouTubePlayer[${instanceId.current}] Error getting time data:`,
            error
          );
        }
      }
    }, 100); // Update 10 times per second

    return () => {
      clearInterval(timeUpdateInterval);
    };
  }, [playerReady, onTimeUpdate]);

  // Initialize player on mount
  useEffect(() => {
    mountedRef.current = true;
    completedRef.current = false;

    // Small delay to ensure DOM is ready
    const initTimer = setTimeout(() => {
      initializePlayer();
    }, 50);

    return () => {
      mountedRef.current = false;
      completedRef.current = true;
      clearTimeout(initTimer);

      console.log(
        `🧹 YouTubePlayer[${instanceId.current}] Cleaning up component`
      );

      // Clear timers
      if (durationTimerRef.current) {
        clearTimeout(durationTimerRef.current);
        durationTimerRef.current = null;
      }

      // Clean up player
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
          playerRef.current = null;
        } catch (e) {
          console.warn(
            `⚠️ YouTubePlayer[${instanceId.current}] Error destroying YouTube player:`,
            e
          );
        }
      }
    };
  }, [initializePlayer]);

  // YouTube player container style
  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  };

  // Apply different styles based on fillMode
  if (fillMode === "cover") {
    containerStyle.position = "relative";
  }

  return (
    <div
      className={`${className} ${
        fillMode === "cover" ? "video-fill-container" : ""
      }`}
      ref={containerRef}
      style={containerStyle}
    >
      {/* Error message overlay with fallback for development */}
      {loadError && (
        <div className="youtube-error-overlay">
          <div className="youtube-error-message">
            <p>Unable to load YouTube video</p>
            {/* Only show retry button in production */}
            {window.location.hostname !== "localhost" && (
              <button
                onClick={() => {
                  setLoadError(null);
                  initializePlayer();
                }}
              >
                Retry
              </button>
            )}
            {/* Show dev message if localhost */}
            {window.location.hostname === "localhost" && (
              <p style={{ fontSize: "14px", opacity: 0.8 }}>
                (YouTube API CORS restriction in local development)
              </p>
            )}
          </div>
          src={getDevFallbackVideo(videoId)}
          {/* Fallback video for development only */}
          {window.location.hostname === "localhost" && (
            <video
              src={DEV_FALLBACK_VIDEOS[videoId] || DEV_FALLBACK_VIDEOS.default}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: fillMode || "cover",
              }}
              autoPlay
              muted={muted}
              loop={loop}
              onEnded={onEnded}
              controls={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(YouTubePlayer);
